import { EUserRole, IUser } from '@okcargo-apps/shared-domain';
import { CreateContactAndUserFromAppCommand, UpdateContactAndUserFromAppCommand } from '@okcargo/command-processor';
import { ContactResponse } from '@okcargo/query-processor';

interface RoleCategoryMapping {
  role: EUserRole;
  category: string;
}

/**
 * Mapping between the app-side roles and the backend contact `categories`.
 *
 * TODO review with the backend. The backend may return more categories than these
 * (CARRIER_OPERATIONS_MANAGER, CARRIER_MANAGER, CARRIER_BILLING, CARRIER_PAYMENTS, CARRIER_OTHERS,
 * SHIPPER_*, DIGITAL_CONTROL_DOCUMENT…). Those are preserved untouched when updating a user.
 */
const ROLE_CATEGORIES: readonly RoleCategoryMapping[] = [
  { role: EUserRole.DRIVER, category: 'CARRIER_DRIVER' },
  { role: EUserRole.CARRIER_ADMIN, category: 'CARRIER_ADMINISTRATIVE' },
  { role: EUserRole.SHIPPER_ADMIN, category: 'SHIPPER_ADMINISTRATIVE' },
  { role: EUserRole.CREATE_DECA_AS_CARRIER, category: 'DCD_CARRIER' },
  { role: EUserRole.CREATE_DECA_AS_SHIPPER, category: 'DCD_SHIPPER' },
  { role: EUserRole.DECA_DRIVER, category: 'DCD_DRIVER' },
];

const ROLE_CATEGORIES_SET: ReadonlySet<string> = new Set(ROLE_CATEGORIES.map((mapping) => mapping.category));

const CARRIER_ROLES: readonly EUserRole[] = [
  EUserRole.DRIVER,
  EUserRole.CARRIER_ADMIN,
  EUserRole.CREATE_DECA_AS_CARRIER,
  EUserRole.DECA_DRIVER,
];

const SHIPPER_ROLES: readonly EUserRole[] = [EUserRole.SHIPPER_ADMIN, EUserRole.CREATE_DECA_AS_SHIPPER];

export function transformToUserRoles(categories: string[]): EUserRole[] {
  return ROLE_CATEGORIES.filter((mapping) => categories.includes(mapping.category)).map((mapping) => mapping.role);
}

export function transformFromUserRoles(roles: EUserRole[]): string[] {
  return ROLE_CATEGORIES.filter((mapping) => roles.includes(mapping.role)).map((mapping) => mapping.category);
}

/** Categories that do not correspond to one of our roles are preserved as they come. */
export function preserveUnknownCategories(categories: string[]): string[] {
  return categories.filter((category) => !ROLE_CATEGORIES_SET.has(category));
}

export function transformToIUser(response: ContactResponse): IUser {
  return {
    id: response.id,
    firstName: response.firstName,
    lastName: response.lastName,
    email: response.emails?.[0]?.value ?? '',
    phone: response.phones?.[0]?.value ?? '',
    categories: response.categories ?? [],
    roles: transformToUserRoles(response.categories ?? []),
    enabled: response.enabled ?? false,
    isCarrierContact: response.isCarrierContact ?? false,
    isShipperContact: response.isShipperContact ?? false,
    userId: response.userId,
    createdAt: toDate(response.createdAt),
    updatedAt: toDate(response.updatedAt),
  };
}

export function transformToIUserList(responses: ContactResponse[]): IUser[] {
  return (responses ?? []).map((response) => transformToIUser(response));
}

function hasAnyRole(roles: EUserRole[], candidates: readonly EUserRole[]): boolean {
  return roles.some((role) => candidates.includes(role));
}

export function transformFromIUserToCreateCommand(user: IUser): CreateContactAndUserFromAppCommand {
  const roles = user.roles ?? [];
  const isCarrier = hasAnyRole(roles, CARRIER_ROLES);
  const isShipper = hasAnyRole(roles, SHIPPER_ROLES);

  return {
    id: user.id || crypto.randomUUID(),
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phone: user.phone,
    categories: transformFromUserRoles(roles),
    enabled: user.enabled,
    isCarrierContact: isCarrier,
    isShipperContact: isShipper,
    shipperContact: isShipper,
    carrierContact: isCarrier,
  };
}

export function transformFromIUserToUpdateCommand(user: IUser): UpdateContactAndUserFromAppCommand {
  const roles = user.roles ?? [];
  const isCarrier = hasAnyRole(roles, CARRIER_ROLES);
  const isShipper = hasAnyRole(roles, SHIPPER_ROLES);

  return {
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    // Only the categories that correspond to our roles are re-derived; the rest are preserved.
    categories: [...preserveUnknownCategories(user.categories ?? []), ...transformFromUserRoles(roles)],
    enabled: user.enabled,
    isCarrierContact: isCarrier,
    isShipperContact: isShipper,
    shipperContact: isShipper,
    carrierContact: isCarrier,
  };
}

function toDate(value: Date | string | undefined | null): Date | undefined {
  return value ? new Date(value) : undefined;
}
